import { GIFT_MAX_CENTS, GIFT_MIN_CENTS } from "../../data/mock-shop";
import { formatShortAmount } from "../../format-money";

export type RecipientType = "" | "family" | "guest";
export type GiftDelivery = "email" | "self";

export function parseGiftAmountCents(
  raw: string,
  currency?: string | null,
): { cents: number; error: "" } | { cents: 0; error: string } {
  // Tolerate a typed currency prefix: "$50", "£50", "NZ$50".
  const text = (raw ?? "")
    .trim()
    .replace(/^\p{L}*\p{Sc}/u, "")
    .replace(/,/g, "");
  if (!text) return { cents: 0, error: "Enter a gift card amount." };
  if (!/^\d*\.?\d*$/.test(text) || text === ".") {
    return {
      cents: 0,
      error: "Enter a gift card amount as a number, e.g. 50.",
    };
  }
  const dollars = Number(text);
  if (!Number.isFinite(dollars)) {
    return {
      cents: 0,
      error: "Enter a gift card amount as a number, e.g. 50.",
    };
  }
  const cents = Math.round(dollars * 100);
  if (Math.abs(dollars * 100 - cents) > 1e-6) {
    return {
      cents: 0,
      error: "Enter a whole number of cents, e.g. 50 or 49.99.",
    };
  }
  if (cents < GIFT_MIN_CENTS || cents > GIFT_MAX_CENTS) {
    return {
      cents: 0,
      error: `Gift card amount must be between ${formatShortAmount(
        GIFT_MIN_CENTS / 100,
        currency,
      )} and ${formatShortAmount(GIFT_MAX_CENTS / 100, currency)}.`,
    };
  }
  return { cents, error: "" };
}

export const RECIPIENT_OPTIONS: readonly {
  value: Exclude<RecipientType, "">;
  label: string;
}[] = [
  { value: "family", label: "Family" },
  { value: "guest", label: "Guest" },
];

export const DEFAULT_TERMS_PARAGRAPHS = [
  "All sales are final unless otherwise stated. Packs and memberships are non-refundable but may be paused or transferred per studio policy.",
  "Cancellations of individual bookings made at least 12 hours before the start time release your spot without penalty.",
  "Memberships renew automatically until cancelled and can be paused or cancelled at any time with no fees.",
  "Gifts are non-refundable and never expire. The recipient receives instructions to claim their account by email.",
];

export const REFERRAL_OPTIONS = [
  "Instagram",
  "TikTok",
  "Google search",
  "Friend or family",
  "Walked by the studio",
  "Email or newsletter",
  "Other",
];

export function formatPurchaseMoney(
  amount: number,
  currency?: string | null,
): string {
  return formatShortAmount(amount, currency);
}
